/**
 * Does the browser believe it has a network?
 *
 * One function, because two different decisions in this dashboard now turn on
 * the answer and they must not drift (D59 — a private copy of a shared decision
 * is a fork, and this one fails silently in both directions):
 *
 * 1. `utils/queryErrorReporting.ts` uses it to decide whether a failed read is a
 *    DEFECT or a fact about the operator's train: an offline failure is filed as
 *    a breadcrumb, not captured as a Sentry error.
 * 2. `features/_shared/readState.ts` uses it to tell a read that was PAUSED for
 *    want of a network apart from one that was never issued for want of a scope.
 *    React Query v4 reports those two identically — see that file.
 *
 * Deliberately a LEAF module with no imports of its own. Both callers sit on
 * opposite sides of the app (the query client at the root, the read classifier
 * inside `features/`), and the protocol's DI prophylactic applies to a browser
 * SPA for the same reason it applies to Nest: a shared constant imported from a
 * file that itself imports services is how an import cycle gets closed.
 *
 * `navigator.onLine` is the only signal available and it is a floor, not a
 * guarantee — it reports `true` for a captive portal or a LAN with no route to
 * the internet, so a `false` here is reliable and a `true` proves nothing. Both
 * callers are built around that asymmetry: each only acts on `false`.
 */

/** `true` when `navigator` says there is a network, or when there is no `navigator`. */
export function isBrowserOnline(): boolean {
    if (typeof navigator === 'undefined') return true;
    return navigator.onLine !== false;
}

/**
 * `true` only when `navigator` positively reports no network.
 *
 * The negation is named rather than left to each caller because the default
 * must be "assume online": an environment without `navigator` (a jsdom test
 * that never set the flag, SSR) must not be treated as offline, or every read
 * in it reclassifies.
 */
export function isBrowserOffline(): boolean {
    return !isBrowserOnline();
}
